import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { translations } from '../translations';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LanguageContext = createContext();

// RTL languages list
const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur'];

// Supported language codes
const SUPPORTED_LANGUAGES = ['sv', 'en', 'pl', 'es', 'ar', 'fr', 'de', 'it', 'pt', 'hr', 'vi', 'tl', 'ko', 'zh', 'ta', 'si', 'ti', 'am'];

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

// Helper to get language from URL path
const getLanguageFromPath = (pathname) => {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length > 0 && SUPPORTED_LANGUAGES.includes(parts[0])) {
    return parts[0];
  }
  return null;
};

// Helper to build localized path
const buildLocalizedPath = (path, lang, defaultLang = 'sv') => {
  // Remove any existing language prefix
  let cleanPath = path;
  const parts = path.split('/').filter(Boolean);
  if (parts.length > 0 && SUPPORTED_LANGUAGES.includes(parts[0])) {
    cleanPath = '/' + parts.slice(1).join('/');
  }
  
  // Don't add prefix for default language (Swedish)
  if (lang === defaultLang) {
    return cleanPath || '/';
  }
  
  // Add language prefix
  return `/${lang}${cleanPath}`;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('sv');
  const [settings, setSettings] = useState({
    translation_enabled: true,
    enabled_languages: ['sv', 'en', 'pl', 'es', 'ar'],
    default_language: 'sv',
  });
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Check if current language is RTL
  const isRTL = RTL_LANGUAGES.includes(language);

  // Update document direction when language changes
  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language, isRTL]);

  // Load settings on mount
  useEffect(() => {
    loadLanguageSettings();
  }, []);

  // Detect language from URL on initial load
  useEffect(() => {
    if (!initialized && !loading) {
      const urlLang = getLanguageFromPath(window.location.pathname);
      if (urlLang && settings.enabled_languages.includes(urlLang)) {
        setLanguage(urlLang);
        localStorage.setItem('language', urlLang);
      }
      setInitialized(true);
    }
  }, [loading, initialized, settings.enabled_languages]);

  const loadLanguageSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/language-settings`);
      setSettings(response.data);
      
      // Check URL language first, then saved language
      const urlLang = getLanguageFromPath(window.location.pathname);
      const savedLang = localStorage.getItem('language');
      
      if (urlLang && response.data.enabled_languages.includes(urlLang)) {
        setLanguage(urlLang);
      } else if (savedLang && response.data.enabled_languages.includes(savedLang)) {
        setLanguage(savedLang);
      } else {
        setLanguage(response.data.default_language);
      }
    } catch (error) {
      console.error('Error loading language settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const t = (key) => {
    // If translation is disabled, always use default language
    const lang = settings.translation_enabled ? language : settings.default_language;
    
    // For languages with static translations, use them
    // For other languages, fall back to the default language (Swedish)
    // Dynamic content will be translated via TranslatedContent component
    return translations[lang]?.[key] 
      || translations[settings.default_language]?.[key] 
      || translations['sv']?.[key] 
      || key;
  };

  const changeLanguage = useCallback((lang, navigate = null) => {
    if (settings.enabled_languages.includes(lang)) {
      setLanguage(lang);
      localStorage.setItem('language', lang);
      
      // Update URL if navigate function is provided
      if (navigate) {
        const currentPath = window.location.pathname;
        const newPath = buildLocalizedPath(currentPath, lang, settings.default_language);
        if (newPath !== currentPath) {
          navigate(newPath, { replace: true });
        }
      }
    }
  }, [settings.enabled_languages, settings.default_language]);

  const getAvailableLanguages = () => {
    return settings.enabled_languages;
  };

  // Check if current language has full static translations
  const hasStaticTranslations = () => {
    return !!translations[language];
  };

  // Get localized path for a given route
  const getLocalizedPath = useCallback((path) => {
    return buildLocalizedPath(path, language, settings.default_language);
  }, [language, settings.default_language]);

  // Get current path without language prefix
  const getCleanPath = useCallback((path) => {
    const parts = path.split('/').filter(Boolean);
    if (parts.length > 0 && SUPPORTED_LANGUAGES.includes(parts[0])) {
      return '/' + parts.slice(1).join('/') || '/';
    }
    return path;
  }, []);

  // Memoize the context value so consumers using React.memo aren't invalidated
  // every render when only an unrelated parent re-renders.
  const contextValue = useMemo(() => ({
    language,
    changeLanguage,
    t,
    settings,
    loading,
    isRTL,
    getAvailableLanguages,
    hasStaticTranslations,
    refreshSettings: loadLanguageSettings,
    getLocalizedPath,
    getCleanPath,
    supportedLanguages: SUPPORTED_LANGUAGES,
  }), [language, changeLanguage, settings, loading, isRTL, getLocalizedPath, getCleanPath]);

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};
